import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { keycloak, KEYCLOAK_INIT_OPTIONS, loginRedirectUri } from "@/lib/keycloak";

export interface AuthUser {
  /** Keycloak `sub` claim — stable user id across sessions. */
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  /**
   * Optional custom claim populated by a Keycloak mapper
   * (see prebuild-docs/14 §5 — `city_id` attribute mapper).
   * Null for users who haven't been scoped to a city yet (e.g. super_admin).
   */
  cityId: string | null;
}

interface AuthContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  /** Realm roles from `realm_access.roles` JWT claim. */
  roles: readonly string[];
  hasRole: (role: string) => boolean;
  hasAnyRole: (...roles: string[]) => boolean;
  /** Raw access token — read on demand, kept fresh via onAuthRefreshSuccess. */
  token: string | null;
  /** Triggers Keycloak login redirect. */
  login: (options?: { redirectUri?: string }) => Promise<void>;
  /** Triggers Keycloak hosted account creation. */
  register: (options?: { redirectUri?: string }) => Promise<void>;
  /** Triggers Keycloak logout + clears local session. */
  logout: (options?: { redirectUri?: string }) => Promise<void>;
  /** Initialisation error (Keycloak unreachable, etc.). Public pages still render. */
  initError: Error | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseUser(
  tokenParsed: Record<string, unknown> | undefined,
): AuthUser | null {
  if (!tokenParsed) return null;
  const sub = tokenParsed.sub;
  if (typeof sub !== "string") return null;
  return {
    id: sub,
    email: typeof tokenParsed.email === "string" ? tokenParsed.email : "",
    name:
      typeof tokenParsed.name === "string"
        ? tokenParsed.name
        : typeof tokenParsed.preferred_username === "string"
          ? tokenParsed.preferred_username
          : "",
    emailVerified: tokenParsed.email_verified === true,
    cityId:
      typeof tokenParsed.city_id === "string" ? tokenParsed.city_id : null,
  };
}

function parseRoles(
  tokenParsed: Record<string, unknown> | undefined,
): string[] {
  const realmAccess = tokenParsed?.realm_access;
  if (
    !realmAccess ||
    typeof realmAccess !== "object" ||
    !("roles" in realmAccess)
  ) {
    return [];
  }
  const roles = (realmAccess as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter((r): r is string => typeof r === "string");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [authedTick, setAuthedTick] = useState(0); // force re-render on token refresh
  const [initError, setInitError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Hook refresh BEFORE init so the very first refresh event is captured.
    keycloak.onAuthRefreshSuccess = () => {
      if (!cancelled) setAuthedTick((t) => t + 1);
    };
    keycloak.onAuthSuccess = () => {
      if (!cancelled) setAuthedTick((t) => t + 1);
    };
    keycloak.onAuthLogout = () => {
      if (!cancelled) setAuthedTick((t) => t + 1);
    };
    keycloak.onTokenExpired = () => {
      // 30s validity target on refresh; on failure, force re-login.
      keycloak.updateToken(30).catch(() => keycloak.login());
    };

    keycloak
      .init(KEYCLOAK_INIT_OPTIONS)
      .then(() => {
        if (!cancelled) {
          setAuthedTick((t) => t + 1);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setInitError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      keycloak.onAuthRefreshSuccess = undefined;
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthLogout = undefined;
      keycloak.onTokenExpired = undefined;
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    // authedTick is intentionally in the deps so this recomputes on every
    // auth-state change from keycloak-js (login, refresh, logout).
    void authedTick;
    const tokenParsed = keycloak.tokenParsed as
      | Record<string, unknown>
      | undefined;
    const user = parseUser(tokenParsed);
    const roles = Object.freeze(parseRoles(tokenParsed));
    return {
      isLoading,
      isAuthenticated: keycloak.authenticated === true,
      user,
      roles,
      hasRole: (role: string) => roles.includes(role),
      hasAnyRole: (...required: string[]) =>
        required.some((r) => roles.includes(r)),
      token: keycloak.token ?? null,
      login: async (options) => {
        await keycloak.login({
          redirectUri: options?.redirectUri ?? loginRedirectUri(),
        });
      },
      register: async (options) => {
        await keycloak.register({
          redirectUri: options?.redirectUri ?? window.location.href,
        });
      },
      logout: async (options) => {
        await keycloak.logout({
          redirectUri: options?.redirectUri ?? window.location.origin,
        });
      },
      initError,
    };
  }, [isLoading, authedTick, initError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
